import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { Chip } from 'primereact/chip'

interface Props {
    children: ReactNode
    fallback?: ReactNode
    onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
    hasError: boolean
    error: Error | null
    errorInfo: ErrorInfo | null
    errorId: string | null
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null
        }
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return {
            hasError: true,
            error,
            errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo)
        this.setState({
            error,
            errorInfo
        })

        if (this.props.onError) {
            this.props.onError(error, errorInfo)
        }
    }

    private handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null
        })
    }

    private handleReload = () => {
        window.location.reload()
    }

    private handleGoHome = () => {
        window.location.href = '/'
    }

    private handleReportBug = () => {
        const subject = encodeURIComponent(`YarlHub Error Report - ${this.state.errorId}`)
        const body = encodeURIComponent(`
YarlHub Error Report:

Error ID: ${this.state.errorId}
URL: ${window.location.href}
Timestamp: ${new Date().toISOString()}
Error Message: ${this.state.error?.message}
`)
        window.open(`mailto:info@yarlhub.com?subject=${subject}&body=${body}`)
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback
            }

            return <ErrorFallback
                error={this.state.error}
                errorId={this.state.errorId}
                onRetry={this.handleRetry}
                onReload={this.handleReload}
                onGoHome={this.handleGoHome}
                onReportBug={this.handleReportBug}
            />
        }

        return this.props.children
    }
}

interface ErrorFallbackProps {
    error: Error | null
    errorId: string | null
    onRetry: () => void
    onReload: () => void
    onGoHome: () => void
    onReportBug: () => void
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
    error,
    errorId,
    onRetry,
    onReload,
    onGoHome,
    onReportBug
}) => {
    const is404Error = error?.message?.includes('404') ||
        error?.message?.includes('Not Found') ||
        window.location.pathname.includes('404')

    return (
        <div className="min-h-screen flex align-items-center justify-content-center p-4 surface-ground">
            <Card className="w-full max-w-30rem p-0 border-round-2xl shadow-6">
                <div className="flex flex-column align-items-center text-center p-4">
                    <div
                        className="flex align-items-center justify-content-center border-circle mb-4"
                        style={{
                            width: '80px',
                            height: '80px',
                            background: 'rgba(211, 84, 0, 0.1)',
                            color: '#d35400'
                        }}
                    >
                        <i className="pi pi-exclamation-circle text-5xl"></i>
                    </div>

                    <h1 className="text-4xl font-bold mb-2 text-900" style={{ fontFamily: 'serif' }}>
                        {is404Error ? '404' : 'Oops!'}
                    </h1>

                    <h2 className="text-xl font-semibold mb-3 text-800">
                        {is404Error ? 'Page Not Found' : 'Something went wrong'}
                    </h2>

                    <p className="text-600 mb-4 line-height-3">
                        {is404Error
                            ? 'The page you are looking for does not exist or has been moved.'
                            : 'An unexpected error occurred. Our team has been notified.'
                        }
                    </p>

                    {errorId && (
                        <Chip
                            label={`ID: ${errorId}`}
                            className="mb-4 bg-orange-50 text-orange-700 font-mono text-xs"
                        />
                    )}

                    <div className="flex flex-column sm:flex-row gap-2 w-full">
                        <Button
                            label="Go Home"
                            icon="pi pi-home"
                            onClick={onGoHome}
                            className="p-button-primary flex-1 bg-orange-600 border-orange-600"
                        />
                        <Button
                            label="Try Again"
                            icon="pi pi-refresh"
                            onClick={onRetry}
                            className="p-button-outlined p-button-secondary flex-1"
                        />
                    </div>

                    <div className="flex gap-3 mt-4">
                        <Button
                            label="Reload"
                            icon="pi pi-sync"
                            className="p-button-text p-button-sm text-600"
                            onClick={onReload}
                        />
                        <Button
                            label="Report"
                            icon="pi pi-flag"
                            className="p-button-text p-button-sm text-600"
                            onClick={onReportBug}
                        />
                    </div>

                    <div className="mt-4 p-3 border-1 border-orange-100 border-round bg-orange-50 w-full">
                        <div className="flex align-items-center justify-content-center gap-2 text-orange-700">
                            <i className="pi pi-envelope"></i>
                            <span className="font-semibold text-sm">Need Help?</span>
                        </div>
                        <a href="mailto:info@yarlhub.com" className="text-orange-600 text-sm no-underline hover:underline block mt-1">
                            info@yarlhub.com
                        </a>
                    </div>
                </div>
            </Card>
        </div>
    )
}

export default ErrorBoundary
